import { NavigationProp, useNavigation } from "@react-navigation/native";
import { Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import dayjs from "dayjs";
import {
  ChargeDetails,
  GroupTicketDetails,
  SeatDetails,
  SimplePlanPackageDetails,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { MyEventsStackParamList } from "navigation/types";
import i18n from "plugins/localization";
import React from "react";
import { View } from "react-native";
import { formatCurrency } from "utils/appUtils";
import { getUserName } from "utils/userUtils";
import TicketAction from "./components/TicketAction";
import { isTicketReservedByGuestComponent } from "./helpers";
interface SeatInfoProps {
  groupCode: string;
  tickets: TicketDetails[];
  groupTickets: GroupTicketDetails[];
  releaseTicket: (ticket: TicketDetails[]) => void;
  reservedByAccountId: number;
  planPackage: SimplePlanPackageDetails | null;
}

const getFormattedAmountPaid = (charge: ChargeDetails) => {
  const {
    priceType,
    totalCreditCardCharges,
    totalPrice,
    totalPoints,
    totalCredits,
  } = charge;
  if (priceType === "CURRENCY") {
    return formatCurrency(totalPrice);
  } else {
    let totalCreditUsage = 0;
    let parsedTotalCreditCardCharges = Number(totalCreditCardCharges);
    let parsedTotalCredits = Number(totalCredits);
    const formattedTotalPoints = formatCurrency(totalPoints, {
      priceType: priceType,
    });
    // price type is POINTS
    if (parsedTotalCreditCardCharges > 0 || parsedTotalCredits > 0) {
      totalCreditUsage = parsedTotalCreditCardCharges + parsedTotalCredits;
    }
    if (!totalCreditUsage) {
      return formattedTotalPoints;
    } else {
      return `${formattedTotalPoints} + ${formatCurrency(totalCreditUsage)}`;
    }
  }
};
const SeatInfo: React.FC<SeatInfoProps> = ({
  groupCode,
  tickets,
  groupTickets,
  releaseTicket,
  reservedByAccountId,
  planPackage,
}) => {
  const { account } = useAccount();

  const navigation =
    useNavigation<NavigationProp<MyEventsStackParamList, "EventDetail">>();

  const withinReleaseWindow = (ticket: TicketDetails) => {
    const subscriptions = account!.subscriptions;
    let releaseWindowMinutes: number = 0;

    for (const subscription of subscriptions) {
      for (const component of subscription.planPackage.components) {
        if (component.id === ticket.planPackageComponentId) {
          releaseWindowMinutes = component.releaseWindowMinutes;
        }
      }
    }

    return dayjs().isBefore(
      dayjs(ticket.event.localDate)
        .tz(ticket.event.timezone)
        .subtract(releaseWindowMinutes, "minutes")
    );
  };

  const shouldShowRelease = (ticket: TicketDetails) => {
    // For ASSOCIATE account, show the release button, since ticket could have been claimed by the account holder.
    if (!planPackage) return true;

    if (planPackage.eventCancelDisabled) return false;
    // AXS does not support releasing single seats from a reservation.
    // So if TenantOptions.ticketProvider = AXS, we will need to remove the individual "X" graphics from the tickets and hide them.
    if (ticket.provider === "AXS") return false;
    // TFL does not support releasing single seats from a reservation.
    if (ticket.provider === "TFL") return false;
    if (ticket.status !== "RESERVED") return false;
    if (isTicketReservedByGuestComponent(ticket.planPackageComponentType)) {
      return true;
    }
    const accountHasGuestPasses = groupTickets.some(
      (groupTicket) =>
        isTicketReservedByGuestComponent(
          groupTicket.planPackageComponentType
        ) && groupTicket.reservedByAccountId === account?.id
    );

    // if account has more than 1 non-guest tickets for current event, we allow them to release one at a time
    if (accountHasGuestPasses) {
      /**
       * Frank cannot release his ticket regardless of transfer status.
       * He cannot release his own single ticket, because he booked guest passes for the event.
       * Doesn't matter who is holding them.
       * He can delete his entire reservation (as long as he still holds all the guest passes in his account and they haven't been transferred yet),
       * or he has to release the guest pass prior to releasing his own ticket.
       */
      return (
        groupTickets.filter(
          (groupTicket) =>
            !isTicketReservedByGuestComponent(
              groupTicket.planPackageComponentType
            ) &&
            groupTicket.accountId === account?.id &&
            groupTicket.status === "RESERVED" &&
            withinReleaseWindow(ticket)
        ).length > 1
      );
    }

    return withinReleaseWindow(ticket);
  };
  const handleReleaseBtnPress = (ticket: TicketDetails) => {
    releaseTicket([ticket]);
  };

  const renderSeatInfo = (
    seat: SeatDetails,
    ticket: TicketDetails | GroupTicketDetails
  ) => {
    const { sectionDisplay, seatDisplay, rowDisplay, ga } = seat;
    const isTicketDetails = "provider" in ticket;
    const shouldShowSeat = isTicketDetails && ticket.provider !== "TFL";

    if (isTicketDetails && ticket.event.type === "PARKING") {
      return <></>;
    }

    return (
      <View>
        {ga ? (
          <Text category="p2">
            {i18n.t("common.generalAdmission") as string} &middot;{" "}
            {i18n.t("common.section") as string}: {sectionDisplay}
          </Text>
        ) : (
          <View>
            {/* Don't show seat for TFL */}
            <Text category="p2" style={{ marginVertical: 3 }}>
              {`Section: ${sectionDisplay} Row: ${rowDisplay}`}
              {shouldShowSeat ? ` Seat: ${seatDisplay}` : ""}
            </Text>
          </View>
        )}
      </View>
    );
  };

  const renderDescription = (ticket: TicketDetails) => {
    const { transferCode, charge } = ticket;
    return (
      <View>
        {renderSeatInfo(ticket.seat, ticket)}
        {charge && ticket.reservedByAccountId === account?.id && (
          <Text category="c1">
            {i18n.t("screens.EventDetail.amountPaid", {
              amount: getFormattedAmountPaid(charge),
            })}
          </Text>
        )}
        {transferCode &&
          // guest pass cannot be transferred
          !isTicketReservedByGuestComponent(
            ticket.planPackageComponentType
          ) && (
            <Text
              style={{
                fontWeight: "bold",
                color: "red",
                fontSize: 12,
              }}
            >
              {i18n.t("screens.EventDetail.voucherTransferInitiated")}
            </Text>
          )}
      </View>
    );
  };

  // filter out tickets that belong to the the current logged in account
  const filterAccountTicket = (ticket: GroupTicketDetails) => {
    return ticket.accountId !== account?.id;
  };

  const toManageGuestScreen = () => {
    navigation.navigate("Guests", { groupCode });
  };

  // If any of the group tickets don't have the same reservedByAccountId
  // as the reservedByAccountId prop, then it is a group reservation.
  // For guest passes, the reservedByAccountId would be the same
  // as the reservedByAccountId prop.
  const isGroupReservation = groupTickets.some(
    (ticket) => ticket.reservedByAccountId !== reservedByAccountId
  );

  return (
    <View>
      <View>
        <BaseCard title={i18n.t("screens.EventDetail.attendees")}>
          {tickets.map((ticket) => {
            const isGuestTicket =
              isTicketReservedByGuestComponent(
                ticket.planPackageComponentType
              ) && ticket.reservedByAccountId === ticket.accountId;
            return (
              <BaseCardListItem
                key={ticket.id}
                title={
                  <Text style={{ fontWeight: "700" }}>
                    {ticket.user
                      ? getUserName(ticket.user!)
                      : `${ticket.planPackageComponentName}`}
                  </Text>
                }
                onPress={isGuestTicket ? toManageGuestScreen : undefined}
                description={renderDescription(ticket)}
                accessoryRight={
                  <TicketAction
                    onReleaseBtnPress={() => handleReleaseBtnPress(ticket)}
                    shouldShowRelease={shouldShowRelease(ticket)}
                    isGroupReservation={isGroupReservation}
                    allowGroupCodeReleases={ticket.event.allowGroupCodeReleases}
                  />
                }
              ></BaseCardListItem>
            );
          })}
          {groupTickets.filter(filterAccountTicket).map((groupTicket) => {
            let title = "";
            if (
              groupTicket.accountId === groupTicket.reservedByAccountId &&
              isTicketReservedByGuestComponent(
                groupTicket.planPackageComponentType
              )
            ) {
              title = i18n.t("screens.EventDetail.guestPending");
            } else {
              title = `${groupTicket.firstName} ${groupTicket.lastName}`;
            }

            return (
              <BaseCardListItem
                key={groupTicket.id}
                title={<Text style={{ fontWeight: "700" }}>{title}</Text>}
                description={renderSeatInfo(groupTicket.seat, groupTicket)}
                accessoryRight={<></>}
              ></BaseCardListItem>
            );
          })}
        </BaseCard>
      </View>
    </View>
  );
};

export default SeatInfo;
